import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Sparkles } from 'lucide-react';
import { isThemeSoundEnabled, setThemeSoundEnabled, playThemeSound } from '../../../utils/themeAudio';

const VALENTINE_MESSAGES = [
    '💘 YOU’RE LOVED! May your gaming journey be pure passion!',
    '🌹 CUPID’S BLESSING: 100% Critical Hit on Store Deals today!',
    '💌 A SECRET VALENTINE has blessed your inventory!',
    '✨ LOVE IS IN THE AIR! Treat yourself to something special!',
    '💖 PURE PERFECTION! You are legendary today and always!'
];

const CupidLetter = () => {
    const [soundOn, setSoundOn] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [loveMessage, setLoveMessage] = useState('');
    const [letterOpened, setLetterOpened] = useState(false);
    const [shootingArrow, setShootingArrow] = useState(false);
    const messageTimeoutRef = useRef(null);

    useEffect(() => {
        setSoundOn(isThemeSoundEnabled());
    }, []);

    const toggleSound = (e) => {
        e.stopPropagation();
        const next = !soundOn;
        setSoundOn(next);
        setThemeSoundEnabled(next);
        if (next) {
            playThemeSound('valentines', 'harp');
        }
    };

    const handleOpenLetter = () => {
        setLetterOpened(true);
        setTimeout(() => setLetterOpened(false), 800);

        // Sound FX
        playThemeSound('valentines', 'harp');
        setTimeout(() => playThemeSound('valentines', 'arrow'), 300);

        // Shoot Cupid's Arrow across screen
        setShootingArrow(true);
        setTimeout(() => setShootingArrow(false), 2200);

        // Random Valentine Message
        const msg = VALENTINE_MESSAGES[Math.floor(Math.random() * VALENTINE_MESSAGES.length)];
        setLoveMessage(msg);
        if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = setTimeout(() => setLoveMessage(''), 2500);
    };

    return (
        <>
            {/* Flying Cupid's Arrow Animation */}
            {shootingArrow && (
                <div className="fixed top-24 md:top-36 -left-48 z-[75] pointer-events-none select-none animate-arrow-fly flex items-center">
                    <span className="text-3xl filter drop-shadow-[0_2px_12px_rgba(244,63,94,0.9)]">
                        🏹 💘 ✨ ✨ ✨
                    </span>
                </div>
            )}

            {/* Global Valentine Banner Popup */}
            {loveMessage && (
                <div className="fixed top-16 md:top-20 inset-x-0 flex justify-center z-[75] pointer-events-none px-4">
                    <div className="w-full max-w-md animate-bounce">
                    <div className="bg-gradient-to-r from-rose-600 via-pink-600 to-rose-600 text-white font-bold text-center px-5 py-3 rounded-2xl shadow-2xl shadow-rose-950/80 border border-pink-300/60 backdrop-blur-md flex items-center justify-center gap-2">
                        <Sparkles className="w-5 h-5 text-yellow-200 animate-spin" />
                        <span className="text-sm tracking-wide text-shadow">{loveMessage}</span>
                    </div>
                </div>
                </div>
            )}

            {/* Bottom-left Love Letter Mascot */}
            <div className="fixed bottom-5 left-4 md:left-8 z-[68] select-none flex flex-col items-start gap-2">
                {/* Speech Tooltip */}
                {hovered && !loveMessage && (
                    <div className="bg-gray-950/95 text-rose-300 text-xs font-semibold px-3 py-1.5 rounded-xl border border-rose-500/50 shadow-lg shadow-black/60 animate-bounce whitespace-nowrap mb-1">
                        Open Cupid’s Love Note... 💌
                        <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-950 border-b border-r border-rose-500/50 rotate-45" />
                    </div>
                )}

                <div className="flex items-center gap-2">
                    {/* The Interactive Love Letter */}
                    <button
                        type="button"
                        onClick={handleOpenLetter}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        className={`group relative outline-none focus:outline-none transition-all duration-300 ${
                            letterOpened ? 'animate-spooky-shake scale-125' : 'animate-float hover:scale-110'
                        }`}
                        title="Click to open Cupid’s secret note!"
                    >
                        {/* Romantic Rose Radiance Aura */}
                        <div className="absolute inset-0 rounded-full bg-rose-500/30 blur-xl group-hover:bg-rose-400/50 transition-all duration-500 pointer-events-none" />

                        {/* Envelope with Wings SVG */}
                        <svg width="60" height="60" viewBox="0 0 100 100" className="relative drop-shadow-[0_6px_20px_rgba(244,63,94,0.8)]">
                            <defs>
                                <linearGradient id="cupidWingL" x1="100%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#ffffff" />
                                    <stop offset="70%" stopColor="#fed7aa" stopOpacity="0.9" />
                                    <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.4" />
                                </linearGradient>
                                <linearGradient id="cupidWingR" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#ffffff" />
                                    <stop offset="70%" stopColor="#fed7aa" stopOpacity="0.9" />
                                    <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.4" />
                                </linearGradient>
                                <linearGradient id="envelopeBody" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#fff1f2" />
                                    <stop offset="100%" stopColor="#fecdd3" />
                                </linearGradient>
                                <linearGradient id="waxHeart" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#fb7185" />
                                    <stop offset="40%" stopColor="#e11d48" />
                                    <stop offset="100%" stopColor="#9f1239" />
                                </linearGradient>
                            </defs>

                            {/* Left Angelic Feather Wing */}
                            <path
                                d="M 22 46 C 4 22 -4 48 16 64 C 7 54 11 44 20 48 Z"
                                fill="url(#cupidWingL)"
                                stroke="#f43f5e"
                                strokeWidth="1"
                                opacity="0.9"
                            />
                            {/* Right Angelic Feather Wing */}
                            <path
                                d="M 78 46 C 96 22 104 48 84 64 C 93 54 89 44 80 48 Z"
                                fill="url(#cupidWingR)"
                                stroke="#f43f5e"
                                strokeWidth="1"
                                opacity="0.9"
                            />

                            {/* Envelope Backing */}
                            <rect x="18" y="30" width="64" height="46" rx="8" fill="url(#envelopeBody)" stroke="#f43f5e" strokeWidth="1.8" />

                            {/* Envelope Lower Fold Flaps */}
                            <path d="M 18 76 L 46 52 L 54 52 L 82 76" stroke="#fb7185" strokeWidth="1.5" fill="none" />
                            {/* Envelope Upper Flap */}
                            <path d="M 18 30 L 50 55 L 82 30" stroke="#f43f5e" strokeWidth="2" fill="#ffe4e6" />

                            {/* Ruby Red Wax Heart Seal */}
                            <path
                                d="M 50 60 C 50 60 41 51 41 46 C 41 42.5 44 40 47.5 40 C 49.5 40 50 42 50 42 C 50 42 50.5 40 52.5 40 C 56 40 59 42.5 59 46 C 59 51 50 60 50 60 Z"
                                fill="url(#waxHeart)"
                                stroke="#881337"
                                strokeWidth="1.2"
                                className="animate-pulse drop-shadow-[0_2px_8px_rgba(225,29,72,0.8)]"
                            />

                            {/* Heart Highlight Glisten */}
                            <circle cx="46" cy="43.5" r="1.5" fill="#ffffff" opacity="0.9" />
                        </svg>
                    </button>

                    {/* Sound Toggle Pill */}
                    <button
                        type="button"
                        onClick={toggleSound}
                        className="px-2.5 py-1.5 rounded-full bg-gray-900/90 hover:bg-gray-800 text-rose-400 border border-rose-500/40 text-[11px] font-bold flex items-center gap-1.5 shadow-md shadow-black/40 transition-all hover:scale-105 active:scale-95"
                        title={soundOn ? 'Valentine Audio Enabled (Click to Mute)' : 'Enable Romantic Harp Audio'}
                    >
                        {soundOn ? (
                            <>
                                <Volume2 className="w-3.5 h-3.5 text-rose-400 animate-pulse" />
                                <span className="hidden sm:inline">Audio ON</span>
                            </>
                        ) : (
                            <>
                                <VolumeX className="w-3.5 h-3.5 text-gray-400" />
                                <span className="hidden sm:inline text-gray-400">Audio OFF</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default CupidLetter;
